// src/index.js

import React from "react";
import ReactDOM from "react-dom/client";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";

import "./index.css";
import App from "./App";
import { initializeOasysTheme } from "./theme";

// Resolve the global OASYS appearance before the React application mounts.
// Dark is the product default; explicit Light/System choices persist locally.
initializeOasysTheme();

// ────────────────────────────────────────────────────────────
// Env (from CRA): REACT_APP_AUTH0_DOMAIN / CLIENT_ID / AUDIENCE
// ────────────────────────────────────────────────────────────
const domain = process.env.REACT_APP_AUTH0_DOMAIN;
const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
const audience = process.env.REACT_APP_AUTH0_AUDIENCE;

if (!domain || !clientId || !audience) {
  throw new Error(
    "Missing one of REACT_APP_AUTH0_DOMAIN, REACT_APP_AUTH0_CLIENT_ID, or REACT_APP_AUTH0_AUDIENCE"
  );
}

// API runtime authority is initialized by App.js.
// Production must provide REACT_APP_API_BASE_URL; local development may use
// App.js's explicit localhost development fallback.

// ────────────────────────────────────────────────────────────
// Auth0 provider wrapper
//  - asks for 'offline_access' so we get a refresh token
//  - uses localstorage to avoid third-party cookie issues
// ────────────────────────────────────────────────────────────
function AuthProvider({ children }) {
  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        audience,
        scope: "openid profile email offline_access",
        redirect_uri: window.location.origin,
      }}
      useRefreshTokens
      cacheLocation="localstorage"
    >
      {children}
    </Auth0Provider>
  );
}

// ────────────────────────────────────────────────────────────
/**
 * App shell helper
 * Keeps the Auth0 token getter exposed for quick local debugging.
 *
 * OASYS Copilot/AdvisorProvider is intentionally owned by App.js because
 * App.js has the active route, tenant/user IDs, navigation handler, and
 * user profile needed for page-aware Copilot context.
 */
// ────────────────────────────────────────────────────────────
function AppShell() {
  const { getAccessTokenSilently } = useAuth0();

  if (typeof window !== "undefined") {
    window.__APP_GET_TOKEN = getAccessTokenSilently;
  }

  return <App />;
}

// ────────────────────────────────────────────────────────────
// Mount main app
// ────────────────────────────────────────────────────────────
const rootEl = document.getElementById("root");
if (rootEl) {
  ReactDOM.createRoot(rootEl).render(
    <React.StrictMode>
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </React.StrictMode>
  );
}
